/**
 * Domain Name service registry panel.
 *
 * Tabs: **Registry** (summary, sync, registration, nameservers, AuthInfo), **DNS zone** (live
 * ``listDNSZone`` read-only table), **Details** (AU / ICANN / DNSSEC / categories / contacts /
 * sync history). Consumes ``/api/domains/services/{id}``.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Sync as SyncIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Visibility as ShowIcon,
  VisibilityOff as HideIcon,
  Shield as ShieldIcon,
  Refresh as RefreshIcon,
  ArticleOutlined as ArticleIcon,
  DnsOutlined as DnsIcon,
  VpnKeyOutlined as KeyIcon,
} from '@mui/icons-material';
import { AccountService } from '../types/account';
import {
  DomainContactCard,
  DomainDnsRecord,
  DomainRegistryInfo,
  DomainRegistryWarning,
  DomainSyncLogEntry,
} from '../types/domainRegistry';
import apiService from '../services/api';

interface DomainRegistryPanelProps {
  service: AccountService;
  /** True for admin users: enables AuthInfo reveal. */
  canViewAuthInfo?: boolean;
}

const SOURCE_LABEL: Record<string, string> = {
  manual: 'Manual',
  scheduled: 'Scheduled',
  bulk_import: 'Bulk import',
};

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return value;
  }
}

function formatDate(value?: string | null): string {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-AU', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return value;
  }
}

function daysUntil(value?: string | null): number | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function expiryColor(value?: string | null): 'default' | 'success' | 'warning' | 'error' {
  const days = daysUntil(value);
  if (days === null) return 'default';
  if (days < 0) return 'error';
  if (days <= 7) return 'error';
  if (days <= 30) return 'warning';
  return 'success';
}

function severityToAlertColor(s: DomainRegistryWarning['severity']) {
  if (s === 'critical') return 'error' as const;
  if (s === 'warning') return 'warning' as const;
  return 'info' as const;
}

function joinAddress(c: DomainContactCard): string {
  const parts = [
    c.address1,
    c.address2,
    c.address3,
    [c.suburb, c.state, c.postcode].filter(Boolean).join(' ').trim() || undefined,
    c.country,
  ].filter((p): p is string => !!p && String(p).trim() !== '');
  return parts.join(', ');
}

function registrationSummary(info: DomainRegistryInfo, expiryDays: number | null): string {
  const parts: string[] = [];
  if (info.status) parts.push(`Status: ${info.status}`);
  if (expiryDays !== null) {
    parts.push(expiryDays < 0 ? 'Expired' : `${expiryDays} day(s) to expiry`);
  }
  if (info.auto_renew) parts.push(`Auto renew: ${info.auto_renew}`);
  return parts.join(' · ') || 'Expiry, renew, and ID protection';
}

function nameserverSummary(info: DomainRegistryInfo): string {
  const n = info.name_servers?.length ?? 0;
  const cfg = info.dns_config_name || info.dns_config;
  if (n === 0) return cfg ? `No NS listed · ${cfg}` : 'No nameservers listed';
  return `${n} nameserver${n === 1 ? '' : 's'}${cfg ? ` · ${cfg}` : ''}`;
}

interface InfoRowProps {
  label: string;
  value?: React.ReactNode;
}

function InfoRow({ label, value }: InfoRowProps) {
  const display = value || value === 0 ? value : '—';
  return (
    <Grid item xs={12}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(148px, 200px) 1fr' },
          columnGap: 2,
          rowGap: 0.25,
          alignItems: 'baseline',
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Box sx={{ minWidth: 0 }}>
          {typeof display === 'string' || typeof display === 'number' ? (
            <Typography variant="body1" sx={{ fontWeight: 500, wordBreak: 'break-word' }}>
              {display}
            </Typography>
          ) : (
            <Box sx={{ '& .MuiTypography-root': { fontWeight: 500 } }}>{display}</Box>
          )}
        </Box>
      </Box>
    </Grid>
  );
}

interface DomainDetailBlockProps {
  title: string;
  children: React.ReactNode;
}

/** Flat bordered block for the Details tab (no accordion). */
function DomainDetailBlock({ title, children }: DomainDetailBlockProps) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1.25, letterSpacing: 0.02 }}>
        {title}
      </Typography>
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          borderRadius: 0,
        }}
      >
        {children}
      </Paper>
    </Box>
  );
}

interface RegistryAccordionSectionProps {
  /** Stable id for accessibility. */
  sectionId: string;
  title: string;
  /** One-line preview shown under the title (visible when collapsed or expanded). */
  summary: string;
  icon?: React.ReactNode;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

function RegistryAccordionSection({
  sectionId,
  title,
  summary,
  icon,
  defaultExpanded,
  children,
}: RegistryAccordionSectionProps) {
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      elevation={0}
      disableGutters
      sx={{
        mb: 2,
        border: 1,
        borderColor: 'divider',
        transition: (theme) =>
          theme.transitions.create(['box-shadow', 'border-color'], { duration: theme.transitions.duration.shortest }),
        '&:before': { display: 'none' },
        '&:hover': {
          borderColor: (theme) => alpha(theme.palette.primary.main, 0.35),
          boxShadow: (theme) => `0 1px 8px ${alpha(theme.palette.common.black, 0.06)}`,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
        aria-controls={`${sectionId}-content`}
        id={`${sectionId}-header`}
        sx={{
          px: 2,
          py: 1.25,
          '& .MuiAccordionSummary-content': {
            alignItems: 'flex-start',
            gap: 1.5,
            my: 0.75,
          },
        }}
      >
        {icon ? (
          <Box sx={{ color: 'primary.main', display: 'flex', pt: 0.25 }} aria-hidden>
            {icon}
          </Box>
        ) : null}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} color="text.primary">
            {title}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {summary}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          px: 2,
          pt: 0,
          pb: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {children}
      </AccordionDetails>
    </Accordion>
  );
}

interface ContactCardProps {
  role: string;
  contact?: DomainContactCard;
}

function ContactCardView({ role, contact }: ContactCardProps) {
  if (!contact) return null;
  const fullName = [contact.firstname, contact.lastname].filter(Boolean).join(' ').trim();
  const address = joinAddress(contact);
  return (
    <Grid item xs={12} md={6}>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            {role}
          </Typography>
          {fullName && (
            <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5 }}>
              {fullName}
            </Typography>
          )}
          {contact.company && (
            <Typography variant="body2" color="text.secondary">
              {contact.company}
            </Typography>
          )}
          {address && (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {address}
            </Typography>
          )}
          <Stack spacing={0.25} sx={{ mt: 1 }}>
            {contact.email ? (
              <Typography variant="body2">
                <strong>Email:</strong> {contact.email}
              </Typography>
            ) : null}
            {contact.phone ? (
              <Typography variant="body2">
                <strong>Phone:</strong> {contact.phone}
              </Typography>
            ) : null}
            {contact.fax ? (
              <Typography variant="body2">
                <strong>Fax:</strong> {contact.fax}
              </Typography>
            ) : null}
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}

const DomainRegistryPanel: React.FC<DomainRegistryPanelProps> = ({
  service,
  canViewAuthInfo,
}) => {
  const [info, setInfo] = useState<DomainRegistryInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<DomainRegistryWarning[]>([]);
  const [history, setHistory] = useState<DomainSyncLogEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [authVisible, setAuthVisible] = useState(false);
  const [authValue, setAuthValue] = useState<string | null>(null);
  const [authLoading, setAuthLoading] = useState(false);
  const [authCopied, setAuthCopied] = useState(false);
  const [domainTab, setDomainTab] = useState(0);
  const [dnsRecords, setDnsRecords] = useState<DomainDnsRecord[]>([]);
  const [dnsLoading, setDnsLoading] = useState(false);
  const [dnsError, setDnsError] = useState<string | null>(null);

  const loadInfo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getDomainRegistryInfo(service.id);
      setInfo(data);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      if (e?.response?.status === 404) {
        setInfo(null);
      } else {
        setError(e?.response?.data?.detail || e?.message || 'Failed to load registry data');
      }
    } finally {
      setLoading(false);
    }
  }, [service.id]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const data = await apiService.getDomainSyncLog(service.id, { limit: 20 });
      setHistory(data.items || []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [service.id]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setError(null);
    try {
      const res = await apiService.syncDomainRegistry(service.id);
      setInfo(res.info);
      setWarnings(res.warnings || []);
      setAuthValue(null);
      setAuthVisible(false);
      if (domainTab === 2) await loadHistory();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(e?.response?.data?.detail || e?.message || 'Registry sync failed');
    } finally {
      setSyncing(false);
    }
  }, [service.id, domainTab, loadHistory]);

  const revealAuth = useCallback(async () => {
    if (authVisible) {
      setAuthVisible(false);
      return;
    }
    if (authValue) {
      setAuthVisible(true);
      return;
    }
    setAuthLoading(true);
    try {
      const data = await apiService.getDomainRegistryInfo(service.id, { includeAuth: true });
      setAuthValue(data.auth_info || '');
      setAuthVisible(true);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e?.response?.data?.detail || 'Failed to reveal AuthInfo');
    } finally {
      setAuthLoading(false);
    }
  }, [authVisible, authValue, service.id]);

  const copyAuth = useCallback(async () => {
    try {
      let value = authValue;
      if (!value) {
        const data = await apiService.getDomainRegistryInfo(service.id, { includeAuth: true });
        value = data.auth_info || '';
        setAuthValue(value);
      }
      if (value) {
        await navigator.clipboard.writeText(value);
        setAuthCopied(true);
        setTimeout(() => setAuthCopied(false), 1500);
      }
    } catch {
      setError('Failed to copy AuthInfo');
    }
  }, [authValue, service.id]);

  const loadDnsRecords = useCallback(async () => {
    setDnsLoading(true);
    setDnsError(null);
    try {
      const data = await apiService.getDomainDnsZone(service.id);
      setDnsRecords(data.records || []);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } }; message?: string };
      setDnsRecords([]);
      setDnsError(e?.response?.data?.detail || e?.message || 'Failed to load DNS zone');
    } finally {
      setDnsLoading(false);
    }
  }, [service.id]);

  useEffect(() => {
    if (domainTab === 1) {
      loadDnsRecords();
    }
  }, [domainTab, loadDnsRecords]);

  useEffect(() => {
    if (domainTab === 2 && info) {
      void loadHistory();
    }
  }, [domainTab, info, loadHistory]);

  const expiry = info?.expiry_at;
  const expiryDays = daysUntil(expiry);
  const idProtectOn = (info?.id_protect || '').toLowerCase() === 'enabled';

  const auData = useMemo(() => {
    if (!info?.au_data) return [];
    return Object.entries(info.au_data)
      .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
      .map(([k, v]) => ({ key: k, value: String(v) }));
  }, [info?.au_data]);

  const dsdata = info?.dsdata || [];
  const categories = info?.categories || [];

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} color="primary.main" sx={{ mb: 0.5 }}>
        Domain (Synergy Wholesale)
      </Typography>
      <Tabs
        value={domainTab}
        onChange={(_, v) => setDomainTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
      >
        <Tab label="Registry" />
        <Tab label="DNS zone" />
        <Tab label="Details" />
      </Tabs>

      {domainTab === 0 && (
        <>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {warnings.map((w, i) => (
            <Alert key={`${w.code}-${i}`} severity={severityToAlertColor(w.severity)} sx={{ mb: 1 }}>
              {w.message}
            </Alert>
          ))}

          {info?.last_sync_status === 'error' && info.last_sync_error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Last sync failed: {info.last_sync_error}
            </Alert>
          )}

          {loading && !info ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2.5,
                mb: 2,
                borderRadius: 0,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <CircularProgress size={24} />
                  <Typography color="text.secondary">Loading registry data…</Typography>
                </Stack>
                <Button variant="contained" startIcon={<SyncIcon />} disabled>
                  Registry Sync
                </Button>
              </Stack>
            </Paper>
          ) : !info ? (
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 2, sm: 2.5 },
                mb: 2,
                borderRadius: 0,
              }}
            >
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                sx={{ mb: 2 }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="overline" color="text.secondary">
                    Synergy Wholesale
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                    {service.service_id}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    No registry cache yet for this service.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                  onClick={handleSync}
                  disabled={syncing}
                  sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
                >
                  {syncing ? 'Syncing…' : 'Registry Sync'}
                </Button>
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Run a sync to fetch domain status, contacts, nameservers, and DNSSEC from Synergy Wholesale.
              </Typography>
            </Paper>
          ) : (
            <>
              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.5 },
                  mb: 2.5,
                  borderRadius: 0,
                }}
              >
                <Stack
                  direction={{ xs: 'column', lg: 'row' }}
                  spacing={2.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', lg: 'flex-start' }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary">
                      Synergy Wholesale registry
                    </Typography>
                    <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25, wordBreak: 'break-word' }}>
                      {info.domain_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                      Last synced <strong>{formatDateTime(info.last_synced_at)}</strong>
                      {info.last_sync_source
                        ? ` · ${SOURCE_LABEL[info.last_sync_source] || info.last_sync_source}`
                        : ''}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                    alignItems="center"
                    justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
                    sx={{ flex: { lg: '0 1 auto' } }}
                  >
                    {info.status ? <Chip size="small" label={info.status} variant="outlined" /> : null}
                    {expiryDays !== null ? (
                      <Chip
                        size="small"
                        color={expiryColor(expiry)}
                        label={
                          expiryDays < 0
                            ? `Expired ${Math.abs(expiryDays)}d ago`
                            : `${expiryDays}d to expiry`
                        }
                      />
                    ) : null}
                    <Chip
                      size="small"
                      label={info.auto_renew || 'Renew —'}
                      color={(info.auto_renew || '').toLowerCase() === 'on' ? 'success' : 'default'}
                      variant="outlined"
                    />
                    <Chip
                      size="small"
                      icon={<ShieldIcon sx={{ fontSize: '1rem !important' }} />}
                      label={info.id_protect || 'ID protect'}
                      color={idProtectOn ? 'primary' : 'default'}
                      variant={idProtectOn ? 'filled' : 'outlined'}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={syncing ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                      onClick={handleSync}
                      disabled={syncing || loading}
                      sx={{ ml: { xs: 0, lg: 0.5 } }}
                    >
                      {syncing ? 'Syncing…' : 'Registry Sync'}
                    </Button>
                  </Stack>
                </Stack>
              </Paper>

              <RegistryAccordionSection
                sectionId="domain-registration"
                title="Registration & policy"
                summary={registrationSummary(info, expiryDays)}
                icon={<ArticleIcon />}
                defaultExpanded
              >
                <Grid container>
                  <InfoRow
                    label="Registry status"
                    value={info.status ? <Chip size="small" label={info.status} variant="outlined" /> : '—'}
                  />
                  <InfoRow
                    label="Expiry"
                    value={
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography component="span" variant="body1" sx={{ fontWeight: 500 }}>
                          {formatDate(expiry)}
                        </Typography>
                        {expiryDays !== null ? (
                          <Chip
                            size="small"
                            color={expiryColor(expiry)}
                            label={
                              expiryDays < 0
                                ? `Expired ${Math.abs(expiryDays)} day(s) ago`
                                : `${expiryDays} day(s) left`
                            }
                          />
                        ) : null}
                      </Stack>
                    }
                  />
                  <InfoRow label="Created at registry" value={formatDate(info.created_at_registry)} />
                  <InfoRow
                    label="Auto renew"
                    value={
                      <Chip
                        size="small"
                        label={info.auto_renew || '—'}
                        color={(info.auto_renew || '').toLowerCase() === 'on' ? 'success' : 'default'}
                        variant="outlined"
                      />
                    }
                  />
                  <InfoRow
                    label="ID Protection"
                    value={
                      <Chip
                        size="small"
                        icon={<ShieldIcon fontSize="small" />}
                        label={info.id_protect || '—'}
                        color={idProtectOn ? 'primary' : 'default'}
                        variant={idProtectOn ? 'filled' : 'outlined'}
                      />
                    }
                  />
                </Grid>
              </RegistryAccordionSection>

              <RegistryAccordionSection
                sectionId="domain-nameservers"
                title="Nameservers & DNS"
                summary={nameserverSummary(info)}
                icon={<DnsIcon />}
                defaultExpanded
              >
                <Grid container>
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                      Nameservers
                    </Typography>
                    {info.name_servers.length === 0 ? (
                      <Typography color="text.secondary">No nameservers returned.</Typography>
                    ) : (
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 0 }}>
                        <Stack spacing={0.5}>
                          {info.name_servers.map((ns, i) => (
                            <Typography key={`${ns}-${i}`} variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {ns}
                            </Typography>
                          ))}
                        </Stack>
                      </Paper>
                    )}
                  </Grid>
                  <InfoRow label="DNS configuration" value={info.dns_config_name || info.dns_config || '—'} />
                </Grid>
              </RegistryAccordionSection>

              <RegistryAccordionSection
                sectionId="domain-authinfo"
                title="AuthInfo / EPP password"
                summary={info.has_auth_info ? 'Reveal or copy (admins only)' : 'Not returned for this TLD or account'}
                icon={<KeyIcon />}
                defaultExpanded
              >
                <Grid container>
                  <Grid item xs={12}>
                    {!info.has_auth_info ? (
                      <Typography color="text.secondary">
                        No password returned by the registry (some TLDs hide this until requested).
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Box
                          component="code"
                          sx={{
                            px: 2,
                            py: 1,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            minWidth: 220,
                            fontFamily: 'monospace',
                            fontSize: '0.95rem',
                          }}
                        >
                          {authVisible && authValue !== null ? authValue || '(empty)' : '••••••••••••'}
                        </Box>
                        {canViewAuthInfo && (
                          <Tooltip title={authVisible ? 'Hide' : 'Show'}>
                            <span>
                              <IconButton size="small" onClick={revealAuth} disabled={authLoading}>
                                {authLoading ? (
                                  <CircularProgress size={16} />
                                ) : authVisible ? (
                                  <HideIcon fontSize="small" />
                                ) : (
                                  <ShowIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canViewAuthInfo && (
                          <Tooltip title={authCopied ? 'Copied!' : 'Copy'}>
                            <span>
                              <IconButton size="small" onClick={copyAuth}>
                                <CopyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {!canViewAuthInfo && (
                          <Typography variant="caption" color="text.secondary">
                            Reveal/copy is restricted to admins.
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              </RegistryAccordionSection>

            </>
          )}
        </>
      )}

      {domainTab === 1 && (
        <Box>
          <Paper
            variant="outlined"
            sx={{
              p: { xs: 2, sm: 2 },
              mb: 2,
              borderRadius: 0,
              bgcolor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.04 : 0.02),
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              justifyContent="space-between"
            >
              <Typography variant="body2" color="text.secondary" sx={{ maxWidth: { md: '75%' } }}>
                DNS records in the Synergy-hosted zone for{' '}
                <strong>{(service.service_id || '').trim() || 'this domain'}</strong>. Read-only for now; an Actions
                column for zone edits will be added here when the API supports it.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={dnsLoading ? <CircularProgress size={14} /> : <RefreshIcon />}
                onClick={loadDnsRecords}
                disabled={dnsLoading}
                sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
              >
                {dnsLoading ? 'Loading…' : 'Reload'}
              </Button>
            </Stack>
          </Paper>
          {dnsError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDnsError(null)}>
              {dnsError}
            </Alert>
          )}
          {dnsLoading && dnsRecords.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : dnsRecords.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 0 }}>
              <Typography color="text.secondary">
                No DNS records returned. The zone may be empty or DNS may be hosted outside Synergy for this domain.
              </Typography>
            </Paper>
          ) : (
            <Paper variant="outlined" sx={{ overflow: 'auto', borderRadius: 0, bgcolor: 'background.paper' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.03),
                    }}
                  >
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', borderBottomColor: 'divider' }}>
                      Host
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', borderBottomColor: 'divider' }}>
                      Type
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', borderBottomColor: 'divider' }}>
                      Content
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', borderBottomColor: 'divider' }}>
                      TTL
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', borderBottomColor: 'divider' }}>
                      Priority
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dnsRecords.map((row) => (
                    <TableRow
                      key={row.record_id || `${row.host_name}-${row.record_type}-${row.content}`}
                      sx={{
                        '&:nth-of-type(even)': {
                          bgcolor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.03 : 0.015),
                        },
                      }}
                    >
                      <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{row.host_name || '—'}</TableCell>
                      <TableCell>
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            color: 'text.secondary',
                            px: 0.75,
                            py: 0.25,
                            borderRadius: 0.5,
                            bgcolor: (theme) => alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.04),
                          }}
                        >
                          {row.record_type || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-word', maxWidth: 360 }}>
                        {row.content || '—'}
                      </TableCell>
                      <TableCell>{row.ttl != null ? String(row.ttl) : '—'}</TableCell>
                      <TableCell>{row.prio != null && row.prio !== '' ? row.prio : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {domainTab === 2 && (
        <Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {warnings.map((w, i) => (
            <Alert key={`${w.code}-${i}`} severity={severityToAlertColor(w.severity)} sx={{ mb: 1 }}>
              {w.message}
            </Alert>
          ))}
          {info?.last_sync_status === 'error' && info.last_sync_error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Last sync failed: {info.last_sync_error}
            </Alert>
          )}
          {loading && !info ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !info ? (
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 0 }}>
              <Typography variant="body2" color="text.secondary">
                Registry data is not loaded yet. Open the <strong>Registry</strong> tab and run{' '}
                <strong>Registry Sync</strong> to load contacts, DNSSEC, ICANN fields, and sync history.
              </Typography>
            </Paper>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Contacts, policy extras, and sync runs. Core expiry and nameservers stay on the{' '}
                <strong>Registry</strong> tab.
              </Typography>
              {auData.length > 0 && (
                <DomainDetailBlock title="AU eligibility">
                  <Grid container>
                    {auData.map(({ key, value }) => (
                      <InfoRow key={key} label={key} value={value} />
                    ))}
                  </Grid>
                </DomainDetailBlock>
              )}
              {(info.icann_status || info.icann_verify_end) && (
                <DomainDetailBlock title="ICANN verification">
                  <Grid container>
                    <InfoRow label="Status" value={info.icann_status} />
                    <InfoRow label="Verification deadline" value={formatDateTime(info.icann_verify_end)} />
                  </Grid>
                </DomainDetailBlock>
              )}
              {dsdata.length > 0 && (
                <DomainDetailBlock title="DNSSEC">
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: (theme) =>
                            alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.03),
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Key tag</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Algorithm</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Digest type</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Digest</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dsdata.map((ds, i) => (
                        <TableRow
                          key={`${ds.UUID || i}`}
                          sx={{
                            '&:nth-of-type(even)': {
                              bgcolor: (theme) =>
                                alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.03 : 0.015),
                            },
                          }}
                        >
                          <TableCell>{String(ds.keyTag ?? '—')}</TableCell>
                          <TableCell>{String(ds.Algorithm ?? ds.Algoirthm ?? '—')}</TableCell>
                          <TableCell>{String(ds.DigestType ?? '—')}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                            {String(ds.Digest ?? '—')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </DomainDetailBlock>
              )}
              {categories.length > 0 && (
                <DomainDetailBlock title="Categories">
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {categories.map((c, i) => (
                      <Chip
                        key={`${c.id || c.name || i}`}
                        label={c.name || c.id || 'Category'}
                        title={c.note || undefined}
                        size="small"
                        variant="outlined"
                        sx={{ borderColor: 'divider' }}
                      />
                    ))}
                  </Stack>
                </DomainDetailBlock>
              )}
              <DomainDetailBlock title="Contacts">
                <Grid container spacing={2}>
                  {idProtectOn && (
                    <Grid item xs={12}>
                      <Alert severity="info" icon={<ShieldIcon />} sx={{ mb: 0 }}>
                        ID Protection is enabled — the contacts below are the protected (private) values.
                      </Alert>
                    </Grid>
                  )}
                  {info.contacts ? (
                    <>
                      <ContactCardView role="Registrant" contact={info.contacts.registrant} />
                      <ContactCardView role="Tech" contact={info.contacts.tech} />
                      <ContactCardView role="Admin" contact={info.contacts.admin} />
                      <ContactCardView role="Billing" contact={info.contacts.billing} />
                      {!info.contacts.registrant &&
                        !info.contacts.tech &&
                        !info.contacts.admin &&
                        !info.contacts.billing && (
                          <Grid item xs={12}>
                            <Typography color="text.secondary">No contact records returned.</Typography>
                          </Grid>
                        )}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Typography color="text.secondary">No contact records cached yet.</Typography>
                    </Grid>
                  )}
                </Grid>
              </DomainDetailBlock>
              <DomainDetailBlock title="Sync history">
                {historyLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={22} />
                  </Box>
                ) : history.length === 0 ? (
                  <Typography color="text.secondary">No sync history yet.</Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          bgcolor: (theme) =>
                            alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.03),
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>When</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Source</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>By</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Duration</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>Notes</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {history.map((row) => (
                        <TableRow
                          key={row.id}
                          sx={{
                            '&:nth-of-type(even)': {
                              bgcolor: (theme) =>
                                alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.03 : 0.015),
                            },
                          }}
                        >
                          <TableCell>{formatDateTime(row.created_at)}</TableCell>
                          <TableCell>{SOURCE_LABEL[row.source] || row.source}</TableCell>
                          <TableCell>
                            {row.triggered_by_name || (row.source === 'scheduled' ? '—' : 'System')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={row.status}
                              color={row.status === 'success' ? 'success' : 'error'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{row.duration_ms != null ? `${row.duration_ms} ms` : '—'}</TableCell>
                          <TableCell sx={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {row.error_message || '—'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </DomainDetailBlock>
            </>
          )}
        </Box>
      )}

      {info && (
        <>
          <Divider sx={{ my: 2.5 }} />
          <Typography variant="caption" color="text.secondary">
            Data sourced from Synergy Wholesale via SOAP. Edits made directly at the registry will appear after the
            next sync.
          </Typography>
        </>
      )}
    </Box>
  );
};

export default DomainRegistryPanel;
